import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BattleModeData } from '../data/BattleModeData';
import { exerciseVideos } from '../data/exerciseVideos';
import { soundManager } from '../utils/SoundManager';
import VillainEncounter from './VillainEncounter';
import FixedNextHelper from '../components/FixedNextHelper';
import './BattleWorkout.css';

const BattleWorkout = () => {
    const navigate = useNavigate();
    const { battleCode: encodedBattleCode, repCount, setCount } = useParams();

    // FIXED: Decode the battle code properly and add debugging
    const battleCode = encodedBattleCode ? decodeURIComponent(encodedBattleCode) : '';

    // Debug logging
    useEffect(() => {
        console.log('BattleWorkout Debug Info:');
        console.log('Encoded battle code from URL:', encodedBattleCode);
        console.log('Decoded battle code:', battleCode);
        console.log('Rep count:', repCount);
        console.log('Set count:', setCount);

        // Save battle code to multiple storage locations for BattleComplete to find
        if (battleCode) {
            sessionStorage.setItem('currentBattleCode', battleCode);
            localStorage.setItem('lastBattleCode', battleCode);
        }
    }, [encodedBattleCode, battleCode, repCount, setCount]);

    // Battle state
    const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
    const [currentSet, setCurrentSet] = useState(1);
    const [battleStats, setBattleStats] = useState({
        attack: 0,
        defend: 0,
        run: 0,
        totalCompleted: 0
    });
    const [generatedWorkout, setGeneratedWorkout] = useState([]);

    const reps = parseInt(repCount);
    const sets = parseInt(setCount);

    // Generate workout from battle code
    useEffect(() => {
        if (battleCode) {
            generateBattleWorkout();
        }
    }, [battleCode]);

    const generateBattleWorkout = () => {
        const exercises = [];
        const characters = battleCode.split('');

        // For each character, get exercises from that muscle group
        characters.forEach(char => {
            const muscleGroupExercises = getMuscleGroupExercises(char);
            if (muscleGroupExercises.length > 0) {
                // Randomly select an exercise from this muscle group
                const randomExercise = muscleGroupExercises[Math.floor(Math.random() * muscleGroupExercises.length)];
                exercises.push(randomExercise);
            }
        });

        setGeneratedWorkout(exercises);
        console.log('Generated workout:', exercises);
    };

    // EXPANDED: Much more comprehensive muscle group mapping with villains mixed in
    const getMuscleGroupExercises = (char) => {
        const exerciseMap = {
            // Bodyweight characters
            '~': ['AirSquats', 'AirSquatsGame', 'Lunges', 'StepUps', 'CalfRaises', 'BodyweightSquats'],
            'w': ['PushUps', 'DiamondPushUps', 'DiamondPushUpsGame', 'ClappingPushUpsGame', 'PikePushUps', 'PikePushUpsGame'],
            '@': ['PullUps', 'ChinUps', 'ChinUpsGame', 'AustralianPullUpsGame', 'BackExtensionsGame', 'AustralianPullUps'],
            'd': ['Plank', 'RussianTwists', 'MountainClimbersGame', 'MountainClimbers', 'ScorpionStretchGame'],
            '^': ['PushUps', 'DiamondPushUps', 'DiamondPushUpsGame', 'ChairTricepExtensionGame', 'TricepDips', 'DipsGame'],
            '_': ['PikePushUps', 'PikePushUpsGame', 'ElevatedPushUps', 'Plank', 'ShoulderPress'],

            // Advanced bodyweight characters
            'e': ['ClappingPushUps', 'ClappingPushUpsGame', 'DiamondPushUpsGame', 'PancakePushUpsGame', 'DeclinePushUps'],
            'k': ['ChinUpsGame', 'BackExtensionsGame', 'AustralianPullUpsGame', 'SeatedCableRowsGame', 'ExplosiveRowsGame'],
            'h': ['DumbbellSquatsandPressGame', 'PushUpJumpsGame', 'ShoulderPress', 'PikePushUps'],
            'm': ['ChairTricepExtensionGame', 'FrontRackStretchGame', 'ArmCircles', 'TricepDips'],
            'i': ['BicepCurlsGame', 'BicepCurls', 'PullUpsBarHold', 'HammerCurls'],
            't': ['DipsGame', 'ChairTricepExtensionGame', 'TricepPushUps', 'TricepDips'],
            'f': ['FarmerSquatsGame', 'WristCurls', 'ForearmPlanks'],
            'g': ['BoxJumpsGame', 'BurpeeWithDumbbellGame', 'JumpingLungesGame', 'StepUpsGame', 'Lunges'],
            'a': ['CrunchAndPunchGame', 'DeadBugsGame', 'KneeKicksGame', 'LegRaisesGame', 'SphinxPushUpsGame'],
            'v': ['JumpSquatsGame', 'CalfRaisesGame', 'CalfRaises', 'JumpingJacks'],
            'p': ['ShrugsGame', 'TrapRaises', 'ShoulderShrugs'],
            'q': ['SumoSquatsGame', 'BodyweightSquats', 'WallSit'],
            'n': ['DeadLiftGame', 'HamstringCurls', 'StiffLeggedDeadlift'],
            'u': ['GluteBridges', 'SingleLegGluteBridge', 'ClamshellExerciseGame'],
            'j': ['ClamshellExerciseGame', 'HipCircles', 'LegSwings'],
            'y': ['WallSit', 'Lunges', 'BodyweightSquats'],
            'z': ['SidePlank', 'RussianTwists', 'ObliqueVUps'],
            '!': ['Burpees', 'SprawlsGame', 'MountainClimbersGame', 'JumpingJacks'],

            // Bands characters
            'l': ['G87DeadLiftGame', 'BandedSquats', 'LegPress', 'BandedLunges'],
            'c': ['G87DoorSmashGame', 'BandedChestPress', 'ChestFlyes', 'BandedPushUps'],
            'b': ['BandedRows', 'BandedLatPulldown', 'RearDeltFlyes'],
            'o': ['BandedWoodchops', 'BandedRussianTwists', 'BandedPlank'],
            'r': ['G87OneArmPunchGame', 'BandedBicepCurls', 'BandedTricepExtensions'],
            's': ['G87StepWideGame', 'G87OneArmOverheadPressGame', 'BandedShoulderPress', 'LateralRaises'],

            // Gym characters
            '$': ['BenchPressGame', 'DumbbellFlyesGame', 'CableCrossoversGame', 'ChestPressMachineGame', 'BenchPress'],
            'x': ['BentOverRowsGame', 'LatPulldownsGame', 'DeadliftsGame', 'BentOverRows'],
            '&': ['ArnoldPressGame', 'LateralRaisesGame', 'ShrugsGame', 'MilitaryPress'],
            '*': ['ArmCurls', 'TricepExtensions', 'ArmPress'],
            '(': ['BicepCurls', 'HammerCurls', 'ConcentrationCurls'],
            ')': ['SkullCrushersGame', 'TricepDips', 'OverheadTricepExtension'],
            '-': ['WristCurls', 'ForearmCurls', 'FarmersWalk'],
            '+': ['PlankVariationsGame', 'CableCrunchesGame', 'AbCrunches', 'SitUps'],
            '=': ['SquatsGame', 'LegPressGame', 'LegCurlsGame', 'Squats', 'LegPress'],
            '[': ['Squats', 'LegExtensions', 'WallSit'],
            ']': ['LegCurls', 'RomanianDeadlifts', 'GoodMornings'],
            '{': ['HipThrusts', 'GluteBridges', 'BulgarianSplitSquats'],
            '}': ['CalfRaises', 'SeatedCalfRaises', 'StandingCalfRaises'],
            ':': ['Shrugs', 'UprightRows', 'FacePulls'],
            ';': ['SidePlank', 'ObliqueVUps', 'WoodChoppers'],
            '|': ['HipAbduction', 'ClamShells', 'HipCircles'],
            '<': ['Lunges', 'StepUps', 'BulgarianSplitSquats'],
            '>': ['MilitaryPressGame', 'Burpees', 'Thrusters', 'FullBodyCircuit']
        };

        return exerciseMap[char] || ['PushUps', 'AirSquats'];
    };

    const currentExercise = generatedWorkout[currentExerciseIndex];
    const isCurrentVillain = currentExercise && BattleModeData.isVillain(currentExercise);

    // FIXED: Exercise video loading function using your exerciseVideos.js
    const getExerciseVideoUrl = (exerciseName) => {
        if (!exerciseName) return null;

        // Remove "Game" suffix for villains when looking for videos
        const cleanExerciseName = exerciseName.replace('Game', '');

        // First try direct mapping
        let videoUrl = exerciseVideos[cleanExerciseName];

        // If not found, try common exercise name mappings for battle mode
        if (!videoUrl) {
            const exerciseMappings = {
                // Banded exercises mapping
                'BandedChestPress': exerciseVideos['G87DoorSmash'] || exerciseVideos['ChestPress'],
                'BandedLatPulldown': exerciseVideos['LatPulldown'] || exerciseVideos['BandedRows'],
                'BandedRows': exerciseVideos['BandedRows'] || exerciseVideos['Rows'],
                'BandedSquats': exerciseVideos['G87WideGripBandSquat'] || exerciseVideos['Squats'],
                'BandedLunges': exerciseVideos['Lunges'],
                'BandedPlank': exerciseVideos['Plank'],
                'BandedRussianTwists': exerciseVideos['RussianTwists'],
                'ChestFlyes': exerciseVideos['ChestFlyes'] || exerciseVideos['DumbbellFlyes'],
                'RearDeltFlyes': exerciseVideos['RearDeltFlyes'] || exerciseVideos['LateralRaises'],
                'LegPress': exerciseVideos['LegPress'] || exerciseVideos['G87DeadLift'],
                'TricepDips': exerciseVideos['TricepDips'] || exerciseVideos['Dips'],
                'TricepPushUps': exerciseVideos['TricepPushUps'] || exerciseVideos['PushUps'],

                // Bodyweight exercises
                'PushUps': exerciseVideos['PushUps'],
                'DiamondPushUps': exerciseVideos['DiamondPushUps'],
                'PikePushUps': exerciseVideos['PikePushUps'],
                'AirSquats': exerciseVideos['AirSquats'] || exerciseVideos['Squats'],
                'BodyweightSquats': exerciseVideos['BodyweightSquats'] || exerciseVideos['Squats'],
                'Lunges': exerciseVideos['Lunges'],
                'Plank': exerciseVideos['Plank'],
                'RussianTwists': exerciseVideos['RussianTwists'],
                'MountainClimbers': exerciseVideos['MountainClimbers'],
                'PullUps': exerciseVideos['PullUps'],
                'ChinUps': exerciseVideos['ChinUps'],
                'CalfRaises': exerciseVideos['CalfRaises'],
                'StepUps': exerciseVideos['StepUps'],
                'Burpees': exerciseVideos['Burpees'],

                // Common fallbacks for exercises that might not have exact matches
                'BandedBicepCurls': exerciseVideos['BicepCurls'],
                'BandedTricepExtensions': exerciseVideos['TricepExtensions'],
                'BandedShoulderPress': exerciseVideos['ShoulderPress'],
                'LateralRaises': exerciseVideos['LateralRaises'],
                'GluteBridges': exerciseVideos['GluteBridges'],
                'SingleLegGluteBridge': exerciseVideos['GluteBridges'],
                'SidePlank': exerciseVideos['SidePlank'] || exerciseVideos['Plank'],
                'WallSit': exerciseVideos['WallSit'],
                'JumpingJacks': exerciseVideos['JumpingJacks']
            };

            videoUrl = exerciseMappings[cleanExerciseName];
        }

        // Debug logging to help identify missing videos
        if (!videoUrl) {
            console.log(`Video not found for exercise: ${cleanExerciseName} (original: ${exerciseName})`);
            console.log('Available exercise videos:', Object.keys(exerciseVideos).slice(0, 10)); // Show first 10 keys
        } else {
            console.log(`Video found for ${cleanExerciseName}: ${videoUrl}`);
        }

        return videoUrl;
    };

    // Handle exercise completion
    const handleExerciseComplete = (exerciseStats = {}) => {
        // Update battle stats
        setBattleStats(prev => ({
            attack: prev.attack + (exerciseStats.attack || 0),
            defend: prev.defend + (exerciseStats.defend || 0),
            run: prev.run + (exerciseStats.run || 0),
            totalCompleted: prev.totalCompleted + reps
        }));

        // Move to next exercise or complete workout
        if (currentExerciseIndex < generatedWorkout.length - 1) {
            setCurrentExerciseIndex(prev => prev + 1);
        } else if (currentSet < sets) {
            // Next set
            setCurrentSet(prev => prev + 1);
            setCurrentExerciseIndex(0);
        } else {
            // Workout complete - calculate final score and navigate
            const totalReps = battleStats.totalCompleted + reps;
            const finalScore = battleStats.attack + battleStats.defend + battleStats.run + totalReps;

            console.log('Navigating to BattleComplete with:');
            console.log('Battle Code:', battleCode);
            console.log('Final Score:', finalScore);
            console.log('Total Reps:', totalReps);
            console.log('Sets:', sets);
            console.log('Current Set:', currentSet);

            // FIXED: Better navigation with multiple fallbacks for battle code
            // Method 1: URL parameters with proper encoding
            const encodedCode = encodeURIComponent(battleCode || 'unknown');

            // Method 2: Also pass via state for backup
            const navState = {
                battleCode: battleCode,
                finalScore: finalScore,
                totalReps: totalReps,
                sets: sets,
                completedSets: currentSet,
                generatedWorkout: generatedWorkout
            };

            // Method 3: Save to sessionStorage as additional backup
            sessionStorage.setItem('battleCompleteData', JSON.stringify({
                battleCode: battleCode,
                finalScore: finalScore,
                totalReps: totalReps,
                sets: sets,
                completedSets: currentSet,
                generatedWorkout: generatedWorkout
            }));

            navigate(`/battle-complete/${encodedCode}/${finalScore}/${totalReps}/${sets}/${currentSet}`, {
                state: navState
            });
        }
    };

    // Handle villain encounter
    const handleVillainComplete = (villainStats) => {
        handleExerciseComplete(villainStats);
    };

    // Calculate progress
    const totalExercises = generatedWorkout.length * sets;
    const completedExercises = (currentSet - 1) * generatedWorkout.length + currentExerciseIndex;
    const progressPercentage = (completedExercises / totalExercises) * 100;

    // Handle floating button click for battle exercises
    const handleFloatingButtonClick = () => {
        // Handle the battle exercise completion with battle stats
        handleExerciseComplete({ attack: 1, defend: 1, run: 1 });
    };

    if (!currentExercise) {
        return <div className="battle-loading">⚔️ Preparing Battle... ⚔️</div>;
    }

    // Show villain encounter if current exercise is a villain
    if (isCurrentVillain) {
        const villainData = BattleModeData.getVillainData(currentExercise);
        return (
            <VillainEncounter
                villain={villainData}
                battleStats={battleStats}
                repCount={reps}
                onVillainComplete={handleVillainComplete}
                onBack={() => navigate('/battle-selection')}
            />
        );
    }

    // FIXED: Regular exercise display with local video support
    const exerciseVideoUrl = getExerciseVideoUrl(currentExercise);

    return (
        <div className="battle-workout-container" style={{ overflow: 'visible', height: 'auto' }}>
            {/* Battle HUD */}
            <div className="battle-hud">
                <div className="battle-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${progressPercentage}%` }}
                        ></div>
                    </div>
                    <span className="progress-text">
                        Set {currentSet}/{sets} • Exercise {currentExerciseIndex + 1}/{generatedWorkout.length}
                    </span>
                </div>

                <div className="battle-stats-display">
                    <div className="stat-circle attack">
                        <span className="stat-label">ATK</span>
                        <span className="stat-value">{battleStats.attack}</span>
                    </div>
                    <div className="stat-circle defend">
                        <span className="stat-label">DEF</span>
                        <span className="stat-value">{battleStats.defend}</span>
                    </div>
                    <div className="stat-circle run">
                        <span className="stat-label">RUN</span>
                        <span className="stat-value">{battleStats.run}</span>
                    </div>
                    <div className="stat-circle total">
                        <span className="stat-label">DONE</span>
                        <span className="stat-value">{battleStats.totalCompleted}</span>
                    </div>
                    <div className="stat-circle current">
                        <span className="stat-label">REPS</span>
                        <span className="stat-value">{reps}</span>
                    </div>
                </div>
            </div>

            {/* Exercise display - full size without scroll bars */}
            <div className="battle-exercise-area" style={{ overflow: 'visible', height: 'auto' }}>
                <div className="exercise-header">
                    <h1 className="exercise-name">{currentExercise}</h1>
                    <p className="exercise-instruction">Complete {reps} reps to continue your battle!</p>
                </div>

                {/* FIXED: Exercise demonstration area with full-size local video support */}
                <div className="exercise-demo" style={{ overflow: 'visible', height: 'auto', maxHeight: 'none' }}>
                    {exerciseVideoUrl ? (
                        <video
                            key={currentExercise} // Force re-render when exercise changes
                            src={exerciseVideoUrl}
                            loop
                            muted
                            autoPlay
                            controls
                            style={{
                                width: '100%',
                                height: 'auto',
                                minHeight: '400px',
                                maxWidth: '100%',
                                borderRadius: '15px',
                                border: '3px solid #FF0000',
                                boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
                                backgroundColor: '#000000',
                                objectFit: 'contain'
                            }}
                        >
                            <p>Your browser doesn't support video playback.</p>
                        </video>
                    ) : (
                        <div className="demo-placeholder" style={{ minHeight: '400px' }}>
                            <span className="demo-icon">💪</span>
                            <p>Exercise Demo</p>
                            <p className="demo-text">{currentExercise}</p>
                        </div>
                    )}
                </div>

                {/* Battle instructions */}
                <div className="battle-instructions">
                    <div className="instruction-item">
                        <span className="instruction-icon">⚔️</span>
                        <span>Stay strong - villains may appear at any moment!</span>
                    </div>
                    <div className="instruction-item">
                        <span className="instruction-icon">💪</span>
                        <span>Each rep completed adds to your battle power</span>
                    </div>
                    <div className="instruction-item">
                        <span className="instruction-icon">🏆</span>
                        <span>Your past workouts provide Hidden Health bonus</span>
                    </div>
                </div>
            </div>

            {/* Use the actual FixedNextHelper component */}
            <FixedNextHelper
                onClick={handleFloatingButtonClick}
            />
        </div>
    );
};

export default BattleWorkout;